import React, { useContext, useState, useReducer } from 'react';
import axios from 'axios';
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
  Navigate,
  useNavigate,
} from 'react-router-dom';
import { library } from '@fortawesome/fontawesome-svg-core';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import Home from './components/pages/home/Index';
import LinkFail from './components/pages/home/LinkFail';
import LinkSuccess from './components/pages/home/LinkSuccess';
import Login from './components/pages/Login';
import LoginTokenForm from './components/pages/LoginToken';
import PageError from './components/shared/PageError';
import PortalLayout from './components/layout/PortalLayout';
import Support from './components/pages/SupportError';
import entities, { entitiesState } from './reducers/entities';
import { useVerifyAuth } from './hooks/useAuth';
import {
  faArrowLeft,
  faBadgeCheck,
  faBars,
  faChevronLeft,
  faChevronRight,
  faLock,
  faLockKeyhole,
  faArrowsRepeat,
  faTriangleExclamation,
} from '@fortawesome/pro-regular-svg-icons';
import { faHouseBlank, faSpinner } from '@fortawesome/pro-solid-svg-icons';
import {
  faArrowRightFromBracket,
  faBuildingColumns,
  faCircleQuestion,
} from '@fortawesome/pro-light-svg-icons';

export const ApiContext = React.createContext();
export const AuthContext = React.createContext();
export const EntitiesContext = React.createContext();
export const UserContext = React.createContext();

const queryClient = new QueryClient();

library.add(
  faChevronLeft,
  faBars,
  faChevronRight,
  faHouseBlank,
  faArrowRightFromBracket,
  faCircleQuestion,
  faSpinner,
  faLock,
  faArrowLeft,
  faBuildingColumns,
  faBadgeCheck,
  faLockKeyhole,
  faArrowsRepeat,
  faTriangleExclamation
);

const headers = {
  Accept: 'application/json',
  'Content-Type': 'application/json',
};

function App() {
  const [auth, setAuth] = useState(null);
  const [user, setUser] = useState(null);
  const [state, dispatch] = useReducer(entities, entitiesState);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthContext.Provider value={{ auth, setAuth }}>
        <ApiContext.Provider
          value={axios.create({
            baseURL: process.env.REACT_APP_API,
            headers: headers,
          })}
        >
          <EntitiesContext.Provider value={{ state, dispatch }}>
            <UserContext.Provider value={{ user, setUser }}>
              <BrowserRouter>
                <PortalLayout>
                  <Routes>
                    <Route
                      exact="true"
                      path="/"
                      element={
                        <RequireAuth>
                          <Home />
                        </RequireAuth>
                      }
                    />
                    <Route
                      exact="true"
                      path="/fail"
                      element={
                        <RequireAuth>
                          <LinkFail />
                        </RequireAuth>
                      }
                    />
                    <Route
                      exact="true"
                      path="/success"
                      element={
                        <RequireAuth>
                          <LinkSuccess />
                        </RequireAuth>
                      }
                    />
                    <Route path="/login" element={<Login />} />
                    <Route path="/support" element={<Support />} />
                    <Route path="/error" element={<PageError />} />
                    <Route
                      path="/login-token/:type/:contact"
                      element={<LoginTokenForm />}
                    />
                  </Routes>
                </PortalLayout>
              </BrowserRouter>
            </UserContext.Provider>
          </EntitiesContext.Provider>
        </ApiContext.Provider>
      </AuthContext.Provider>
    </QueryClientProvider>
  );
}

function RequireAuth({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const api = useContext(ApiContext);
  const auth = useVerifyAuth();

  if (!auth) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  api.interceptors.response.use(
    function (response) {
      return response;
    },
    function (error) {
      if (error.response.status === 403) navigate('/');

      return Promise.reject(error);
    },
  );
  api.defaults.headers.common[
    'Authorization'
  ] = `Bearer ${auth?.token.access_token}`;

  return children;
}

export default App;
